/**
 * OverheadDB — the Overhead master database (Search → Overhead): every bill that
 * isn't job material or payroll — vehicles, insurance, app, cellular, 407,
 * bonuses, subscriptions. Same tooling as the vendor databases: Scan email
 * (every email in a date range — attachments AND text receipts), QB import
 * (QuickBooks purchases + bills), Import PDFs, nightly auto-scan; duplicates
 * always flagged, never double-logged. Each row's category and renewal date
 * are editable; the Accounting report's overhead cards read from here.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, FileUp, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import ScanMailButton from "./ScanMailButton";
import RenewalCalendar from "./RenewalCalendar";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TH = "px-2.5 py-1.5 text-left font-mono text-[9px] uppercase tracking-widest text-white/50";
const TD = "px-2.5 py-1.5 text-xs text-white/85";
const CATS = ["vehicles", "insurance", "app", "cellular", "407", "bonuses", "subscriptions", "other"];
const SOURCE_LABEL = { "email scan": "Email", "auto scan": "Auto scan", import: "PDF", quickbooks: "QuickBooks", manual: "Manual" };

export default function OverheadDB() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [uploading, setUploading] = useState(false);
  const [autoOn, setAutoOn] = useState(false);
  const [autoTime, setAutoTime] = useState("00:01");
  const [sel, setSel] = useState(() => new Set());
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ vendor: "", category: "other", date: "", amount: "", invoice_number: "", description: "", renewal_date: "", recurring: false });
  const [editing, setEditing] = useState(null); // expense_id whose renewal date is being edited
  const [showCal, setShowCal] = useState(false);
  const [renew, setRenew] = useState("");
  const fileRef = useRef(null);

  const load = async (silent = true) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/overhead/expenses", { params: { year } });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load overhead"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year]);
  useEffect(() => {
    api.get("/overhead/config").then((r) => { setAutoOn(!!r.data.auto_scan); setAutoTime(r.data.auto_scan_time || "00:01"); }).catch(() => {});
  }, []);

  const saveAuto = async (on, time) => {
    setAutoOn(on); setAutoTime(time);
    try {
      await api.put("/overhead/config", { auto_scan: on, auto_scan_time: time });
      toast.success(on ? `Auto scan on — every day at ${time}` : "Auto scan off");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      [...files].forEach((f) => fd.append("files", f));
      const r = await api.post("/overhead/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const bad = r.data.results.filter((x) => !x.ok);
      toast[bad.length ? "warning" : "success"](`${r.data.imported} logged${bad.length ? ` · ${bad.length} skipped (${bad.map((b) => `${b.file}: ${b.error}`).join("; ")})` : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const patch = async (id, body) => {
    try {
      const r = await api.patch(`/overhead/expenses/${id}`, body);
      setData((d) => ({ ...d, expenses: d.expenses.map((x) => (x.expense_id === id ? r.data : x)) }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const remove = async (ids) => {
    if (!window.confirm(`Delete ${ids.length} overhead ${ids.length === 1 ? "entry" : "entries"}? This can't be undone.`)) return;
    try {
      await api.post("/overhead/expenses/bulk-delete", { ids });
      setSel(new Set()); load();
      toast.success(`Deleted ${ids.length}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };
  const addManual = async () => {
    try {
      await api.post("/overhead/expenses", { ...form, amount: Number(form.amount) });
      toast.success(`${form.vendor} · ${fmtMoney(form.amount)} logged`);
      setAdding(false); setForm({ vendor: "", category: "other", date: "", amount: "", invoice_number: "", description: "", renewal_date: "", recurring: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };

  const rows = data?.expenses || [];
  const years = [...new Set([...(data?.years || []), String(new Date().getFullYear())])].sort().reverse();
  const inp = "rounded-md border border-white/15 bg-white/5 px-2 py-1.5 text-xs text-white [color-scheme:dark] placeholder:text-white/25 focus:border-[#4f6fe8] focus:outline-none";

  return (
    <div className="space-y-4" data-testid="overhead-db">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Overhead · master database · bills, subscriptions & renewals</p>
        <div className="flex flex-wrap items-center gap-2">
          <ScanMailButton vendor="overhead" title="overhead" basePath="/overhead" onDone={load} />
          <ScanMailButton vendor="overhead" title="overhead" basePath="/overhead" onDone={load} mode="qb" />
          <input ref={fileRef} type="file" accept=".pdf,application/pdf" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="overhead-import-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="overhead-import-btn"
            className="flex items-center gap-2 rounded-md bg-[#1a3fd1] px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1633a3] disabled:opacity-50">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} Import PDFs
          </button>
          <button type="button" onClick={() => setShowCal((v) => !v)} data-testid="overhead-calendar-btn" aria-pressed={showCal}
            className={`flex items-center gap-2 rounded-md border px-3 py-2 font-mono text-[11px] font-semibold uppercase tracking-widest ${showCal ? "border-amber-500/60 bg-amber-500/15 text-amber-200" : "border-white/15 text-white/80 hover:border-[#1a3fd1] hover:text-white"}`}>
            <CalendarDays className="h-4 w-4" /> Renewals
          </button>
          <button type="button" onClick={() => setAdding((a) => !a)} data-testid="overhead-add-btn"
            className="flex items-center gap-2 rounded-md border border-white/15 px-3 py-2 font-mono text-[11px] font-semibold uppercase tracking-widest text-white/80 hover:border-[#1a3fd1] hover:text-white">
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
      </div>

      {/* auto scan — no account lock: overhead spans many vendors/accounts */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2" data-testid="overhead-auto-scan">
        <button type="button" role="switch" aria-checked={autoOn} onClick={() => saveAuto(!autoOn, autoTime)} data-testid="overhead-auto-toggle"
          className={`flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors ${autoOn ? "justify-end bg-[#1a3fd1]" : "justify-start bg-white/15"}`}>
          <span className="h-4 w-4 rounded-full bg-white shadow" />
        </button>
        <span className="font-mono text-[10px] uppercase tracking-widest text-white/60">Auto scan</span>
        <input type="time" value={autoTime} disabled={!autoOn} onChange={(e) => setAutoTime(e.target.value)} onBlur={() => autoOn && saveAuto(true, autoTime)}
          data-testid="overhead-auto-time" className={`${inp} w-28 disabled:opacity-40`} />
        <span className="font-mono text-[10px] text-white/40">{autoOn ? `reads every email from the 24 hours before ${autoTime}, every day — new bills are logged, possible duplicates wait for you here` : "off"}</span>
      </div>

      {adding && (
        <div className="grid gap-2 rounded-lg border border-white/10 bg-white/[0.02] p-3 sm:grid-cols-4" data-testid="overhead-add-form">
          <input placeholder="Vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className={inp} data-testid="overhead-add-vendor" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inp} data-testid="overhead-add-category">
            {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inp} data-testid="overhead-add-date" />
          <input type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inp} data-testid="overhead-add-amount" />
          <input placeholder="Invoice / receipt #" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className={inp} />
          <input placeholder="What is it" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inp} sm:col-span-2`} />
          <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white/60">Renews <input type="date" value={form.renewal_date} onChange={(e) => setForm({ ...form, renewal_date: e.target.value, recurring: true })} className={inp} /></label>
          <button type="button" onClick={addManual} disabled={!form.vendor || !form.date || !form.amount} data-testid="overhead-add-save"
            className="rounded-md bg-[#1a3fd1] px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-white disabled:opacity-40 sm:col-start-4">Save</button>
        </div>
      )}

      {showCal && <RenewalCalendar />}

      {/* category cards */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8" data-testid="overhead-categories">
        {(data?.categories || []).map((c) => (
          <div key={c.category} className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`overhead-cat-${c.category}`}>
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50">{c.label}</p>
            <p className="mt-1 font-mono text-base font-bold tabular-nums text-white">{fmtMoney(c.total)}</p>
            <p className="font-mono text-[10px] text-white/50">{c.count} bill{c.count === 1 ? "" : "s"}{c.next_renewal ? ` · renews ${c.next_renewal}` : ""}</p>
          </div>
        ))}
      </div>

      {/* table */}
      <div className="rounded-lg border border-white/10 bg-white/[0.02]">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/60">{rows.length} entries · {fmtMoney(data?.total)}</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={inp} data-testid="overhead-year">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          {sel.size > 0 && (
            <button type="button" onClick={() => remove([...sel])} data-testid="overhead-bulk-delete"
              className="inline-flex items-center gap-1 rounded-md border border-red-500/40 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-red-300 hover:bg-red-500/10"><Trash2 className="h-3 w-3" /> Delete {sel.size}</button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse">
            <thead><tr className="bg-white/5">
              <th className={TH}><input type="checkbox" checked={rows.length > 0 && sel.size === rows.length} onChange={(e) => setSel(e.target.checked ? new Set(rows.map((r) => r.expense_id)) : new Set())} data-testid="overhead-select-all" /></th>
              <th className={TH}>Date</th><th className={TH}>Vendor</th><th className={TH}>Category</th><th className={TH}>What</th>
              <th className={TH}>Inv / acct #</th><th className={TH}>Renews</th><th className={TH}>Source</th><th className={`${TH} text-right`}>Amount</th><th className={TH}></th>
            </tr></thead>
            <tbody>
              {loading && !data ? (
                <tr><td colSpan={10} className="px-3 py-6 text-center font-mono text-[11px] text-white/50"><Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={10} className="px-3 py-6 text-center font-mono text-[11px] text-white/50" data-testid="overhead-empty">Nothing logged for {year} — scan email, pull from QuickBooks or import a PDF.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.expense_id} className="border-t border-white/5" data-testid={`overhead-row-${r.expense_id}`}>
                  <td className={TD}><input type="checkbox" checked={sel.has(r.expense_id)} onChange={(e) => setSel((s) => { const n = new Set(s); e.target.checked ? n.add(r.expense_id) : n.delete(r.expense_id); return n; })} /></td>
                  <td className={`${TD} whitespace-nowrap`}>{r.date}</td>
                  <td className={`${TD} font-semibold text-white`}>{r.vendor}</td>
                  <td className={TD}>
                    <select value={r.category} onChange={(e) => patch(r.expense_id, { category: e.target.value })} data-testid={`overhead-cat-select-${r.expense_id}`}
                      className="rounded border border-white/10 bg-transparent px-1 py-0.5 font-mono text-[10px] uppercase tracking-wider text-white/80 [color-scheme:dark]">
                      {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className={`${TD} max-w-[260px] truncate`} title={r.description}>{r.description || "—"}</td>
                  <td className={`${TD} font-mono text-[11px]`}>{r.invoice_number || r.account_number || "—"}</td>
                  <td className={`${TD} whitespace-nowrap`}>
                    {editing === r.expense_id ? (
                      <input type="date" autoFocus value={renew} onChange={(e) => setRenew(e.target.value)}
                        onBlur={() => { patch(r.expense_id, { renewal_date: renew, recurring: !!renew }); setEditing(null); }}
                        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setEditing(null); }}
                        className={`${inp} w-36`} data-testid={`overhead-renew-input-${r.expense_id}`} />
                    ) : (
                      <button type="button" onClick={() => { setRenew(r.renewal_date || ""); setEditing(r.expense_id); }} data-testid={`overhead-renew-${r.expense_id}`}
                        className={`inline-flex items-center gap-1 ${r.renewal_date ? "text-amber-300" : "text-white/40"} hover:text-white`}>
                        {r.renewal_date || "set"} <Pencil className="h-3 w-3" />
                      </button>
                    )}
                  </td>
                  <td className={TD}><span className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white/60" title={r.source_file}>{SOURCE_LABEL[r.source] || r.source}</span></td>
                  <td className={`${TD} text-right font-mono tabular-nums text-white`}>{fmtMoney(r.amount)}</td>
                  <td className={TD}><button type="button" onClick={() => remove([r.expense_id])} aria-label="Delete" data-testid={`overhead-delete-${r.expense_id}`} className="text-white/30 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
